// vessel_images_manager.jsx
// Manage a vessel's Cloudflare photo gallery against the live
// navela-vessel-images API. Images are per-vessel: pick a vessel, then
// upload (multipart), delete, or set one as the vessel's primary photo_url.

import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Images, Search, Trash2, RefreshCw, Star, ImageOff, Check, Upload, Loader2, Ship, X,
} from 'lucide-react';
import { useNavela } from '../../authentication_components/navelaContext';
import { Button, Loading, EmptyState, ConfirmDialog, inputClass } from './navela_ui';

const MAX_UPLOAD = 15 * 1024 * 1024; // 15MB, matches the backend limit.

function CardImage({ url, alt }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) {
    return <div className="flex h-40 w-full items-center justify-center rounded-t-xl bg-slate-100 text-slate-300"><ImageOff className="h-8 w-8" /></div>;
  }
  return <img src={url} alt={alt} onError={() => setBroken(true)} loading="lazy" className="h-40 w-full rounded-t-xl object-cover" />;
}

export default function VesselImagesManager() {
  const { fetchVessels, getVessel, fetchVesselImages, uploadVesselImage, deleteVesselImage, setPrimaryImage } = useNavela();

  // Vessel picker
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [vessel, setVessel] = useState(null); // selected vessel (with photo_url)

  // Images for the selected vessel
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [settingPrimary, setSettingPrimary] = useState(null);
  const fileInputRef = useRef(null);

  // Debounced vessel search.
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const data = await fetchVessels({ search: query.trim(), limit: 8 });
        setResults(data?.vessels ?? []);
        setShowResults(true);
      } catch { /* surfaced by context */ }
    }, 300);
    return () => clearTimeout(t);
  }, [query, fetchVessels]);

  const loadImages = useCallback(async (vesselId) => {
    setLoadingImages(true);
    try {
      const data = await fetchVesselImages(vesselId);
      setImages(data?.images ?? []);
    } catch (err) {
      toast.error(err.message || 'Failed to load images');
    } finally {
      setLoadingImages(false);
    }
  }, [fetchVesselImages]);

  const selectVessel = async (v) => {
    setShowResults(false);
    setQuery('');
    setResults([]);
    setVessel(v);
    loadImages(v.id);
  };

  // Re-read the vessel (fresh photo_url) + its images, e.g. after set-primary.
  const refresh = useCallback(async () => {
    if (!vessel) return;
    try {
      const fresh = await getVessel(vessel.id);
      if (fresh) setVessel(fresh);
    } catch { /* keep the stale vessel */ }
    loadImages(vessel.id);
  }, [vessel, getVessel, loadImages]);

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file || !vessel) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files are allowed'); return; }
    if (file.size > MAX_UPLOAD) { toast.error('File exceeds the 15MB limit'); return; }
    setUploading(true);
    try {
      await uploadVesselImage(vessel.id, file);
      toast.success('Image uploaded');
      loadImages(vessel.id);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const makePrimary = async (img) => {
    if (!vessel) return;
    setSettingPrimary(img.id);
    try {
      await setPrimaryImage(vessel.id, img.id);
      toast.success('Set as primary photo');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to set primary');
    } finally {
      setSettingPrimary(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !vessel) return;
    setDeleting(true);
    try {
      await deleteVesselImage(vessel.id, deleteTarget.id);
      toast.success('Image deleted');
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const isPrimary = (img) => vessel?.photo_url && img.cloudflare_image_url === vessel.photo_url;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <Images className="h-5 w-5 text-pr-navy-600" /> Vessel Images
          </h2>
          <p className="text-sm text-slate-500">Cloudflare photo gallery, managed per vessel</p>
        </div>
      </div>

      {/* Vessel picker */}
      <div className="relative mb-5 max-w-xl">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length && setShowResults(true)}
            placeholder="Search a vessel by name or IMO to manage its photos…"
            className={`${inputClass} pl-9`}
          />
        </div>
        {showResults && results.length > 0 && (
          <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
            {results.map((v) => (
              <button
                key={v.id}
                onClick={() => selectVessel(v)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50"
              >
                {v.photo_url
                  ? <img src={v.photo_url} alt={v.name} className="h-8 w-11 rounded object-cover ring-1 ring-slate-200" />
                  : <div className="flex h-8 w-11 items-center justify-center rounded bg-slate-100 text-slate-300"><Ship className="h-4 w-4" /></div>}
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-800">{v.name}</div>
                  <div className="text-xs text-slate-400">#{v.id}{v.cruise_line ? ` · ${v.cruise_line}` : ''}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {!vessel ? (
        <EmptyState icon={Images} title="Pick a vessel" hint="Search for a vessel above to view, upload and manage its Cloudflare photos." />
      ) : (
        <>
          {/* Selected vessel header */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-pr-navy-100 bg-pr-navy-50/40 px-4 py-3">
            <div className="flex items-center gap-3">
              {vessel.photo_url
                ? <img src={vessel.photo_url} alt={vessel.name} className="h-12 w-16 rounded-lg object-cover ring-1 ring-pr-navy-100" />
                : <div className="flex h-12 w-16 items-center justify-center rounded-lg bg-white text-slate-300 ring-1 ring-pr-navy-100"><Ship className="h-5 w-5" /></div>}
              <div>
                <div className="font-bold text-slate-800">{vessel.name}</div>
                <div className="text-xs text-slate-500">#{vessel.id}{vessel.cruise_line ? ` · ${vessel.cruise_line}` : ''} · {images.length} photo{images.length === 1 ? '' : 's'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={refresh} title="Refresh"><RefreshCw className={`h-4 w-4 ${loadingImages ? 'animate-spin' : ''}`} /></Button>
              <Button onClick={onPickFile} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? 'Uploading…' : 'Upload photo'}
              </Button>
              <button onClick={() => { setVessel(null); setImages([]); }} className="rounded-lg p-2 text-slate-400 hover:bg-white hover:text-slate-600" title="Clear selection"><X className="h-4 w-4" /></button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChosen} />
            </div>
          </div>

          {loadingImages && images.length === 0 ? (
            <Loading label="Loading images…" />
          ) : images.length === 0 ? (
            <EmptyState icon={Images} title="No photos yet" hint="Upload the first photo for this vessel. The one you set as primary becomes its hero image across the app." action={<Button onClick={onPickFile}><Upload className="h-4 w-4" /> Upload photo</Button>} />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {images.map((img) => {
                const primary = isPrimary(img);
                return (
                  <div key={img.id} className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
                    <div className="relative">
                      <CardImage url={img.cloudflare_image_url} alt={img.original_filename || `Vessel ${img.vessel_id}`} />
                      {primary && (
                        <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-amber-400/95 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950 shadow">
                          <Star className="h-3 w-3 fill-current" /> Primary
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="truncate text-sm font-semibold text-slate-800">{img.original_filename || <span className="text-slate-400">Untitled</span>}</p>
                      <p className="mt-0.5 truncate text-xs text-slate-400">{img.upload_source || img.content_type || img.cloudflare_image_id}</p>
                      <div className="mt-3 flex items-center justify-between">
                        {primary ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600"><Check className="h-3.5 w-3.5" /> Hero photo</span>
                        ) : (
                          <button onClick={() => makePrimary(img)} disabled={settingPrimary === img.id} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-amber-600 hover:bg-amber-50 disabled:opacity-50" title="Make primary">
                            {settingPrimary === img.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />} Make primary
                          </button>
                        )}
                        <button onClick={() => setDeleteTarget(img)} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete image?"
        message={deleteTarget ? 'This photo will be removed from the vessel gallery and from Cloudflare. If it is the primary photo, the vessel’s hero image will be cleared.' : ''}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
